"use client";
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import CategoryDto from "@/projectRoot/api-client/interfaces/models/products/product-entities/Category.dto";
import BrandDto from "@/projectRoot/api-client/interfaces/models/products/product-entities/Brand.dto";
import LabelDto from "@/projectRoot/api-client/interfaces/models/products/product-entities/Label.dto";
import CollectionDto from "@/projectRoot/api-client/interfaces/models/products/product-entities/Collection.dto";
import ColorDto from "@/projectRoot/api-client/interfaces/models/products/product-entities/Color.dto";
import FilterStatsDto, { FilterStatsCountDto, FilterStatsRangeDto } from "@/projectRoot/api-client/interfaces/response/products/FilterStats.dto";
import ProductsSearchDto, { AvailableSortByProperties } from "@/projectRoot/api-client/interfaces/request/products/ProductsSearch.dto";
import { debounce } from "lodash";
import MetaFilter from "@/projectRoot/api-client/interfaces/response/products/MetaFilter.dto";

export interface FiltersProps {
    options?: {
        categories?: CategoryDto[];
        brands?: BrandDto[];
        labels?: LabelDto[];
        collections?: CollectionDto[];
        colors?: ColorDto[];
        sizes?: FilterStatsCountDto[];
        prices?: FilterStatsRangeDto;
    };
    state?: ProductsSearchDto;
    meta?: MetaFilter<FilterStatsDto>;
}

const initialState: ProductsSearchDto = {};

export type FilterAction = 
    | { type: "updateSearch", payload?: string }
    | { type: "updateCategories", payload: string }
    | { type: "updateAncestorCategories", payload: string }
    | { type: "updateBrands", payload: string }
    | { type: "updateLabels", payload: string }
    | { type: "updateCollections", payload: string }
    | { type: "updateColors", payload: string }
    | { type: "updateSizes", payload: string }
    | { type: "updateSizeGuides", payload: string }
    | { type: "updatePage", payload: number }
    | { type: "updateSortBy", payload: AvailableSortByProperties }
    | { type: "updatePriceRange", payload?: FilterStatsRangeDto }
    | { type: "setFilters", payload?: ProductsSearchDto }
    | { type: "resetFilters" }

export function reducer(state: ProductsSearchDto, action: FilterAction): ProductsSearchDto {
    let tempState = state;
    switch (action.type) {
        case 'updateSearch':
            return {
                ...tempState,
                search: action.payload
            };
        case 'setFilters':
            return action.payload ?? initialState;
        case 'updateCategories':
            
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.category_ids?.includes(action.payload)) {
                return {
                    ...tempState,
                    category_ids: tempState.category_ids?.filter(categoryId => categoryId !== action.payload),
                }
            } else {
                return {
                    ...tempState,
                    category_ids: tempState.category_ids ? tempState.category_ids.concat([action.payload]) : [action.payload],
                }
            }
        case 'updateAncestorCategories':
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.category_ids) {
                delete tempState.category_ids;
            }
            if (action.payload != '') {
                tempState.category_ancestor_ids = 
                    tempState.category_ancestor_ids?.includes(action.payload)
                    ?
                    tempState.category_ancestor_ids?.filter(categoryId => categoryId !== action.payload)
                    :
                    [action.payload]
            } else {
                delete tempState.category_ancestor_ids
            }
            return {
                ...tempState
            }
        case 'updateBrands':
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.brand_ids?.includes(action.payload)) {
                return {
                    ...tempState,
                    brand_ids: tempState.brand_ids?.filter(brandId => brandId !== action.payload),
                }
            } else {
                return {
                    ...tempState,
                    brand_ids: tempState.brand_ids ? tempState.brand_ids.concat([action.payload]) : [action.payload],
                }
            }
        case 'updateLabels':
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.label_ids?.includes(action.payload)) {
                return {
                    ...tempState,
                    label_ids: tempState.label_ids?.filter(labelId => labelId !== action.payload),
                }
            } else {
                return {
                    ...tempState,
                    label_ids: tempState.label_ids ? tempState.label_ids.concat([action.payload]) : [action.payload],
                }
            }
        case 'updateCollections':
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.collection_ids?.includes(action.payload)) {
                return {
                    ...tempState,
                    collection_ids: tempState.collection_ids?.filter(collectionId => collectionId !== action.payload),
                }
            } else {
                return {
                    ...tempState,
                    collection_ids: tempState.collection_ids ? tempState.collection_ids.concat([action.payload]) : [action.payload],
                }
            }
        case 'updateColors':
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.color_ids?.includes(action.payload)) {
                return {
                    ...tempState,
                    color_ids: tempState.color_ids?.filter(colorId => colorId !== action.payload),
                }
            } else {
                return {
                    ...tempState,
                    color_ids: tempState.color_ids ? tempState.color_ids.concat([action.payload]) : [action.payload],
                }
            }
        case 'updateSizes':
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.stock_variants?.includes(action.payload)) {
                return {
                    ...tempState,
                    stock_variants: tempState.stock_variants?.filter(variant => variant !== action.payload),
                }
            } else {
                return {
                    ...tempState,
                    stock_variants: tempState.stock_variants ? tempState.stock_variants.concat([action.payload]) : [action.payload],
                }
            }
        case 'updateSizeGuides':
            if (tempState.page) {
                delete tempState.page;
            }
            if (tempState.sort_by) {
                delete tempState.sort_by;
            }
            if (tempState.guides?.includes(action.payload)) {
                return {
                    ...tempState,
                    guides: tempState.guides?.filter(variant => variant !== action.payload),
                }
            } else {
                return {
                    ...tempState,
                    guides: tempState.guides ? tempState.guides.concat([action.payload]) : [action.payload],
                }
            }
        case 'updatePage':
            return {
                ...tempState,
                page: action.payload,
            }
        case 'updateSortBy':
            return {
                ...tempState,
                sort_by: action.payload,
            }
        case 'updatePriceRange':
            if (typeof action.payload !== 'undefined') {
                return {
                    ...state,
                    price_min: action.payload.min,
                    price_max: action.payload.max,
                }
            } 
            const { price_min, price_max, ...rest } = state;
            // let newState = state;
            // delete newState.price_min;
            // delete newState.price_max;
            return {
                ...rest
            };

            
        case 'resetFilters':
            return {...initialState};
        default:
            return initialState;
    }
}

export interface FiltersContextProps {
    state: ProductsSearchDto;
    filters: FiltersProps;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
    dispatch: (value: FilterAction) => void;
    onSubmitFilters: () => void;
    productsCount?: number;
    setProductsCount?: (value: number) => void;
    filters_count: number;
}

export const defaultFilters : FiltersContextProps = {
    state: {},
    filters: {},
    loading: false,
    setLoading: () => {},
    dispatch: (value: FilterAction) => undefined,
    onSubmitFilters: () => undefined,
    filters_count: 0,
}

export const FiltersContext = createContext<FiltersContextProps>(defaultFilters)

export type Props = Partial<FiltersContextProps> & {
    children?: any
}

export function FiltersProvider({
    children,
    filters
}: Partial<Props>) {
    const [state, dispatch] = useReducer(reducer, filters.state ?? initialState);
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [isSetState, setIsSetState] = useState<boolean>(false);
    const [count, setCount] = useState<number>(0);

    const totalObjectLength = useMemo(() => {
        let count = 0;
        let properties = Object.entries(state);
        properties.forEach(([key, value]) => {
            if(typeof value != 'string' && value.length > 0){
                count += value.length;
            }else if(typeof value != 'object' && key !== 'sort_by' && key !== 'page' && key !== 'price_max'){
                count += 1;
            }
        });
        return count;
    }, [state]);

    const onSubmitFilters = useCallback(() => {
        let endpoint: string = [
            state.category_ids?.map(cat => `category_ids[]=${cat}`).join('&') ?? '',
            state.category_ancestor_ids?.map(cat => `category_ancestor_ids[]=${cat}`).join('&') ?? '',
            state.brand_ids?.map(brand => `brand_ids[]=${brand}`).join('&') ?? '',
            state.label_ids?.map(label => `label_ids[]=${label}`).join('&') ?? '',
            state.collection_ids?.map(col => `collection_ids[]=${col}`).join('&') ?? '',
            state.color_ids?.map(color => `color_ids[]=${color}`).join('&') ?? '',
            state.stock_variants?.map(variant => `stock_variants[]=${variant}`).join('&') ?? '',
            state.guides?.map(guide => `guides[]=${guide}`).join('&') ?? '',
            state.price_min ? `price_min=${state.price_min}` : '',
            state.price_max ? `price_max=${state.price_max}` : '',
            state.search ? `search=${state.search}` : '',
            state.page ? `page=${state.page}` : '',
            state.sort_by ? `sort_by=${state.sort_by}` : '',
        ].filter(string => string.length !== 0).join('&');

        router.push(`${pathname}?${endpoint}`);

    }, [pathname, router, state]);

    useEffect(() => {
        if (isSetState) {
            setLoading(true);
            const debouncedSearch = debounce(() => {
                onSubmitFilters();
            }, 1000);
            debouncedSearch();
            return () => {
                debouncedSearch.cancel();
            }
        }
        setIsSetState(true);
    }, [state]);

    useEffect(() => {
        setLoading(false);
    }, [searchParams]);

    useEffect(() => {
        if (searchParams.size === 0) {
            dispatch({ type: 'resetFilters' });
            setIsSetState(false);
        }
    }, [pathname]);

    return (
        <FiltersContext.Provider 
            value={{ 
                filters,
                state,
                dispatch,
                loading,
                setLoading,
                onSubmitFilters,
                productsCount: count,
                setProductsCount: setCount,
                filters_count: totalObjectLength
             }}
        >
            {children}
        </FiltersContext.Provider>
    )
}

//context hooks
export function useFilters(): FiltersContextProps {
    return useContext<FiltersContextProps>(FiltersContext);
}